import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
  Tabs,
  Divider,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  BugReport as TestIcon
} from '@mui/icons-material';
// import { useAuth } from '../contexts/AuthContext'; // سيتم استخدامه لاحقاً للتحقق من الصلاحيات
import { getFirebaseSettings, saveFirebaseSettings, FirebaseSettings } from '../services/firebaseSettingsService';
import { resetDatabase } from '../services/databaseService';
import dropboxService, { DropboxBackupSettings } from '../services/dropboxService';
import {
  getMaintenanceBankSettings,
  saveMaintenanceBankSettings,
  validateProviderSettings,
  testProviderConnection,

} from '../services/maintenanceBankSettingsService';
import {
  getImageStorageSettings,
  saveImageStorageSettings,
  ImageStorageType,
  ImageStorageSettings
} from '../services/imageStorageService';
import { ImageProtectionType } from '../utils/types';
import { getImgBBSettings, saveImgBBSettings, ImgBBSettings } from '../services/imgbbService';
import { getImageKitSettings, saveImageKitSettings, ImageKitSettings } from '../services/imagekitService';

import { getMaintenanceImageSettings, saveMaintenanceImageSettings } from '../services/maintenanceImageSettingsService';

// واجهة لعرض محتوى التبويب
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// مكون عرض محتوى التبويب
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// خصائص التبويب
const a11yProps = (index: number) => {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
};

const AdvancedSettingsPage: React.FC = () => {
  // يمكن استخدام currentUser للتحقق من الصلاحيات في المستقبل
  // const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  // Image settings have been moved to UnifiedImageSettingsPage

  // حالة إعدادات ImgBB
  const [imgbbSettings, setImgbbSettings] = useState<ImgBBSettings>({
    apiKey: '',
    enabled: false
  });
  const [loadingImgBB, setLoadingImgBB] = useState(false);
  const [savingImgBB, setSavingImgBB] = useState(false);

  // حالة إعدادات ImageKit
  const [imagekitSettings, setImagekitSettings] = useState<ImageKitSettings>({
    publicKey: '',
    privateKey: '',
    urlEndpoint: '',
    enabled: false
  });
  const [loadingImageKit, setLoadingImageKit] = useState(false);
  const [savingImageKit, setSavingImageKit] = useState(false);

  // حالة إعدادات تخزين الصور
  const [imageStorageSettings, setImageStorageSettings] = useState<ImageStorageSettings>({
    storageType: ImageStorageType.IMGBB,
    preferredDisplaySource: 'imgbb',
    imageProtection: ImageProtectionType.NONE,
    watermarkText: ''
  });
  const [loadingImageStorage, setLoadingImageStorage] = useState(false);
  const [savingImageStorage, setSavingImageStorage] = useState(false);

  // حالة إعدادات Firebase
  const [firebaseSettings, setFirebaseSettings] = useState<FirebaseSettings>({
    apiKey: "",
    authDomain: "",
    projectId: "",
    storageBucket: "",
    messagingSenderId: "",
    appId: ""
  });
  const [loadingFirebase, setLoadingFirebase] = useState(false);
  const [savingFirebase, setSavingFirebase] = useState(false);

  // حالة إعادة تعيين قاعدة البيانات
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [expectedAnswer, setExpectedAnswer] = useState('');
  const [question, setQuestion] = useState('');
  const [resetting, setResetting] = useState(false);

  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // حالة إعدادات Dropbox
  const [settings, setSettings] = useState<DropboxBackupSettings>({
    enabled: false,
    autoUpload: false,
    autoDelete: false,
    maxBackups: 10,
    folderName: 'MaintenanceSystemBackups',
    accessToken: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);
  const [isDropboxConnected, setIsDropboxConnected] = useState<boolean>(false);

  // حالة إعدادات رفع صور الصيانة
  const [maintenanceImageSettings, setMaintenanceImageSettings] = useState({
    provider: 'imgbb' as 'imgbb' | 'imagekit' | 'cloudinary',
    compressionEnabled: true,
    compressionQuality: 80,
    maxImageSize: 1920,
    autoUpload: true,
    fallbackProvider: 'imgbb' as 'imgbb' | 'imagekit' | 'cloudinary' | undefined,
    retryAttempts: 3
  });
  const [loadingMaintenanceImages, setLoadingMaintenanceImages] = useState(false);
  const [savingMaintenanceImages, setSavingMaintenanceImages] = useState(false);

  // حالة إعدادات بنك الصيانة
  const [maintenanceBankSettings, setMaintenanceBankSettings] = useState({
    imageProvider: 'imgbb' as 'imgbb' | 'imagekit',
    useImageStorageSettings: true, // استخدام إعدادات تخزين الصور
    imgbbApiKey: '',
    imagekitPublicKey: '',
    imagekitPrivateKey: '',
    imagekitUrlEndpoint: '',
    maxImageSize: 5, // بالميجابايت
    allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    compressionEnabled: true,
    compressionQuality: 85,
    autoBackup: true,
    backupFrequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
    maxImagesPerTopic: 10,
    requireApproval: false,
    allowPublicAccess: true
  });
  const [loadingMaintenanceBank, setLoadingMaintenanceBank] = useState(false);
  const [savingMaintenanceBank, setSavingMaintenanceBank] = useState(false);

  // تغيير التبويب النشط
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // دالة تحميل إعدادات بنك الصيانة
  const loadMaintenanceBankSettings = async () => {
    try {
      setLoadingMaintenanceBank(true);
      const bankSettings = await getMaintenanceBankSettings();

      // تعيين الخيار الافتراضي لاستخدام إعدادات تخزين الصور إذا لم تكن مفاتيح API محددة
      const shouldUseImageStorage = !bankSettings.imgbbApiKey && !bankSettings.imagekitPublicKey;

      setMaintenanceBankSettings({
        ...bankSettings,
        useImageStorageSettings: shouldUseImageStorage
      });
    } catch (error) {
      console.error('Error loading maintenance bank settings:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل إعدادات بنك الصيانة',
        severity: 'error'
      });
    } finally {
      setLoadingMaintenanceBank(false);
    }
  };

  // دالة حفظ إعدادات بنك الصيانة
  const saveMaintenanceBankSettingsHandler = async () => {
    try {
      setSavingMaintenanceBank(true);

      // إعداد البيانات لحفظ بنك الصيانة
      let settingsToSave = { ...maintenanceBankSettings };

      // إذا كان يستخدم إعدادات تخزين الصور، لا نحفظ مفاتيح API المنفصلة
      if (maintenanceBankSettings.useImageStorageSettings) {
        settingsToSave.imgbbApiKey = '';
        settingsToSave.imagekitPublicKey = '';
        settingsToSave.imagekitPrivateKey = '';
        settingsToSave.imagekitUrlEndpoint = '';
      }

      // التحقق من صحة الإعدادات
      const validation = validateProviderSettings(settingsToSave);
      if (!validation.valid) {
        setSnackbar({
          open: true,
          message: `خطأ في الإعدادات: ${validation.errors.join(', ')}`,
          severity: 'error'
        });
        return;
      }

      // حفظ الإعدادات في Firebase
      await saveMaintenanceBankSettings(settingsToSave);

      // حفظ مفاتيح API في localStorage للاستخدام الفوري فقط إذا لم يكن يستخدم إعدادات تخزين الصور
      if (!maintenanceBankSettings.useImageStorageSettings) {
        if (maintenanceBankSettings.imageProvider === 'imgbb' && maintenanceBankSettings.imgbbApiKey) {
          // استيراد الخدمة ديناميكياً لتجنب المشاكل
          const { ImgBBService } = await import('../services/imageUploadService');
          ImgBBService.setApiKey(maintenanceBankSettings.imgbbApiKey);
        }
      }

      setSnackbar({
        open: true,
        message: `تم حفظ إعدادات بنك الصيانة بنجاح (${maintenanceBankSettings.useImageStorageSettings ? 'باستخدام إعدادات تخزين الصور' : 'بمفاتيح منفصلة'})`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving maintenance bank settings:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات بنك الصيانة',
        severity: 'error'
      });
    } finally {
      setSavingMaintenanceBank(false);
    }
  };

  // دالة اختبار الاتصال بمزود الخدمة
  const testMaintenanceBankConnection = async () => {
    try {
      let settingsToTest = { ...maintenanceBankSettings };

      // إذا كان يستخدم إعدادات تخزين الصور، استخدم مفاتيح API من هناك
      if (maintenanceBankSettings.useImageStorageSettings) {
        if (maintenanceBankSettings.imageProvider === 'imgbb') {
          settingsToTest.imgbbApiKey = imgbbSettings.apiKey;
        } else if (maintenanceBankSettings.imageProvider === 'imagekit') {
          settingsToTest.imagekitPublicKey = imagekitSettings.publicKey;
          settingsToTest.imagekitPrivateKey = imagekitSettings.privateKey;
          settingsToTest.imagekitUrlEndpoint = imagekitSettings.urlEndpoint;
        }
      }

      const result = await testProviderConnection(settingsToTest);
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'فشل في اختبار الاتصال',
        severity: 'error'
      });
    }
  };

  // اختبار إعدادات رفع الصور
  const handleTestImageSettings = async () => {
    try {
      setTesting(true);

      // استيراد خدمة الاختبار
      const { ImageUploadService } = await import('../services/imageUploadService');
      const result = await ImageUploadService.testSettings();

      console.log('🧪 نتيجة اختبار الإعدادات:', result);

      setSnackbar({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error'
      });

      // عرض تفاصيل إضافية في وحدة التحكم
      if (result.details) {
        console.table(result.details);
      }

    } catch (error: any) {
      console.error('❌ خطأ في اختبار الإعدادات:', error);
      setSnackbar({
        open: true,
        message: `فشل في اختبار الإعدادات: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setTesting(false);
    }
  };

  // نسخ إعدادات المنتجات لبنك الصيانة
  const handleCopyProductSettings = async () => {
    try {
      setTesting(true);

      console.log('📋 نسخ إعدادات المنتجات لبنك الصيانة...');

      // جلب إعدادات تخزين الصور المستخدمة للمنتجات
      const { getImageStorageSettings } = await import('../services/imageStorageService');
      const storageSettings = await getImageStorageSettings();

      console.log('📊 إعدادات تخزين الصور:', storageSettings);

      // تحديد المزود والمفاتيح
      let newSettings = {
        ...maintenanceBankSettings,
        useImageStorageSettings: true // تفعيل استخدام إعدادات تخزين الصور
      };

      if (storageSettings.storageType.includes('imgbb')) {
        // محاولة جلب مفتاح ImgBB
        try {
          const { getImgBBSettings } = await import('../services/imgbbService');
          const imgbbSettings = await getImgBBSettings();

          if (imgbbSettings.apiKey) {
            newSettings.imageProvider = 'imgbb';
            console.log('✅ تم نسخ إعدادات ImgBB لبنك الصيانة');
          }
        } catch (error) {
          console.warn('⚠️ فشل جلب إعدادات ImgBB:', error);
        }
      }

      if (storageSettings.storageType.includes('imagekit')) {
        // محاولة جلب إعدادات ImageKit
        try {
          const { getImageKitSettings } = await import('../services/imagekitService');
          const imagekitSettings = await getImageKitSettings();

          if (imagekitSettings.publicKey && imagekitSettings.privateKey && imagekitSettings.urlEndpoint) {
            newSettings.imageProvider = 'imagekit';
            console.log('✅ تم نسخ إعدادات ImageKit لبنك الصيانة');
          }
        } catch (error) {
          console.warn('⚠️ فشل جلب إعدادات ImageKit:', error);
        }
      }

      // تحديث الحالة
      setMaintenanceBankSettings(newSettings);

      setSnackbar({
        open: true,
        message: `تم تكوين بنك الصيانة لاستخدام إعدادات تخزين الصور (${newSettings.imageProvider}). يرجى حفظ الإعدادات.`,
        severity: 'success'
      });

    } catch (error: any) {
      console.error('❌ خطأ في نسخ إعدادات المنتجات:', error);
      setSnackbar({
        open: true,
        message: `فشل في نسخ إعدادات المنتجات: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setTesting(false);
    }
  };

  // تحميل الإعدادات عند تحميل الصفحة
  useEffect(() => {
    const loadSettings = async () => {
      // تحميل إعدادات Firebase
      try {
        setLoadingFirebase(true);
        const firebaseData = await getFirebaseSettings();
        setFirebaseSettings(firebaseData);
      } catch (error) {
        console.error('Error loading Firebase settings:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تحميل إعدادات Firebase',
          severity: 'error'
        });
      } finally {
        setLoadingFirebase(false);
      }

      // تحميل إعدادات Dropbox
      try {
        setLoading(true);
        const settings = await dropboxService.getDropboxBackupSettings();
        setSettings(settings);
      } catch (error) {
        console.error('Error loading Dropbox settings:', error);
        setSnackbar({
          open: true,
          message: 'فشل في تحميل إعدادات Dropbox',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }

      // تحميل إعدادات رفع صور الصيانة من Firebase
      try {
        setLoadingMaintenanceImages(true);
        const maintenanceImageData = await getMaintenanceImageSettings();

        // تحويل البيانات لتطابق نوع State
        setMaintenanceImageSettings({
          provider: maintenanceImageData.provider,
          compressionEnabled: maintenanceImageData.compressionEnabled,
          compressionQuality: maintenanceImageData.compressionQuality,
          maxImageSize: maintenanceImageData.maxImageSize,
          autoUpload: maintenanceImageData.autoUpload,
          fallbackProvider: maintenanceImageData.fallbackProvider || 'imgbb',
          retryAttempts: maintenanceImageData.retryAttempts || 3
        });

        console.log('✅ تم تحميل إعدادات صور الصيانة من Firebase:', maintenanceImageData);
      } catch (error) {
        console.error('Error loading maintenance image settings:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تحميل إعدادات رفع صور الصيانة',
          severity: 'error'
        });
      } finally {
        setLoadingMaintenanceImages(false);
      }

      // تحميل إعدادات ImgBB
      try {
        setLoadingImgBB(true);
        const imgbbData = await getImgBBSettings();
        setImgbbSettings(imgbbData);
      } catch (error) {
        console.error('Error loading ImgBB settings:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تحميل إعدادات ImgBB',
          severity: 'error'
        });
      } finally {
        setLoadingImgBB(false);
      }

      // تحميل إعدادات ImageKit
      try {
        setLoadingImageKit(true);
        const imagekitData = await getImageKitSettings();
        setImagekitSettings(imagekitData);
      } catch (error) {
        console.error('Error loading ImageKit settings:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تحميل إعدادات ImageKit',
          severity: 'error'
        });
      } finally {
        setLoadingImageKit(false);
      }

      // تحميل إعدادات تخزين الصور
      try {
        setLoadingImageStorage(true);
        const imageStorageData = await getImageStorageSettings();
        setImageStorageSettings(imageStorageData);
      } catch (error) {
        console.error('Error loading image storage settings:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تحميل إعدادات تخزين الصور',
          severity: 'error'
        });
      } finally {
        setLoadingImageStorage(false);
      }
    };

    loadSettings();
    checkDropboxConnection();
    loadMaintenanceBankSettings();
  }, []);

  // تحميل إعدادات Dropbox
  const checkDropboxConnection = async () => {
    try {
      const settings = await dropboxService.getDropboxBackupSettings();
      setIsDropboxConnected(!!settings.accessToken);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
      setIsDropboxConnected(false);
    }
  };

  // تحديث إعدادات ImgBB
  const handleImgBBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setImgbbSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // حفظ إعدادات ImgBB
  const handleSaveImgBB = async () => {
    try {
      setSavingImgBB(true);
      await saveImgBBSettings(imgbbSettings);
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات ImgBB بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving ImgBB settings:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات ImgBB',
        severity: 'error'
      });
    } finally {
      setSavingImgBB(false);
    }
  };

  // تحديث إعدادات ImageKit.io
  const handleImageKitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setImagekitSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // حفظ إعدادات ImageKit.io
  const handleSaveImageKit = async () => {
    try {
      setSavingImageKit(true);
      await saveImageKitSettings(imagekitSettings);
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات ImageKit.io بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving ImageKit.io settings:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات ImageKit.io',
        severity: 'error'
      });
    } finally {
      setSavingImageKit(false);
    }
  };

  // تحديث إعدادات تخزين الصور
  const handleImageStorageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setImageStorageSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // حفظ إعدادات تخزين الصور
  const handleSaveImageStorage = async () => {
    try {
      setSavingImageStorage(true);
      await saveImageStorageSettings(imageStorageSettings);
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات تخزين الصور بنجاح',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving image storage settings:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات تخزين الصور',
        severity: 'error'
      });
    } finally {
      setSavingImageStorage(false);
    }
  };

  // تحديث إعدادات Firebase
  const handleFirebaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFirebaseSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // حفظ إعدادات Firebase
  const handleSaveFirebase = async () => {
    try {
      setSavingFirebase(true);
      await saveFirebaseSettings(firebaseSettings);
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات Firebase بنجاح. يرجى إعادة تشغيل التطبيق لتطبيق التغييرات.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving Firebase settings:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات Firebase',
        severity: 'error'
      });
    } finally {
      setSavingFirebase(false);
    }
  };

  // إنشاء سؤال عشوائي للتأكيد
  const generateRandomQuestion = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const answer = num1 + num2;

    setExpectedAnswer(answer.toString());
    setQuestion(`${num1} + ${num2} = ?`);
  };

  // فتح نافذة حوار إعادة تعيين قاعدة البيانات
  const handleOpenResetDialog = () => {
    setResetConfirmation('');
    setOpenResetDialog(true);
    generateRandomQuestion();
  };

  // إغلاق نافذة حوار إعادة تعيين قاعدة البيانات
  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
    setResetConfirmation('');
  };

  // إعادة تعيين قاعدة البيانات
  const handleResetDatabase = async () => {
    if (resetConfirmation !== expectedAnswer) {
      setSnackbar({
        open: true,
        message: 'الإجابة غير صحيحة. لم يتم إعادة تعيين قاعدة البيانات.',
        severity: 'error'
      });
      return;
    }

    try {
      setResetting(true);
      await resetDatabase();
      setSnackbar({
        open: true,
        message: 'تم إعادة تعيين قاعدة البيانات بنجاح',
        severity: 'success'
      });
      handleCloseResetDialog();
    } catch (error) {
      console.error('Error resetting database:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إعادة تعيين قاعدة البيانات',
        severity: 'error'
      });
    } finally {
      setResetting(false);
    }
  };

  // إغلاق الإشعار
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // تحديث الإعدادات
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // حفظ الإعدادات
  const handleSave = async () => {
    try {
      setSaving(true);
      await dropboxService.saveDropboxBackupSettings(settings);
      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات Dropbox بنجاح',
        severity: 'success'
      });
      await checkDropboxConnection();
    } catch (error) {
      console.error('Error saving Dropbox settings:', error);
      setSnackbar({
        open: true,
        message: 'فشل في حفظ إعدادات Dropbox',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // اختبار الاتصال بـ Dropbox
  const handleTestConnection = async () => {
    try {
      setTesting(true);

      // التحقق من وجود رمز الوصول
      if (!settings.accessToken) {
        throw new Error('يرجى إدخال رمز الوصول أولاً');
      }

      // اختبار الاتصال
      await dropboxService.getDropboxBackups();

      setSnackbar({
        open: true,
        message: 'تم الاتصال بـ Dropbox بنجاح',
        severity: 'success'
      });

      setIsDropboxConnected(true);
    } catch (error) {
      console.error('Error testing Dropbox connection:', error);
      setSnackbar({
        open: true,
        message: 'فشل في الاتصال بـ Dropbox: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'),
        severity: 'error'
      });
      setIsDropboxConnected(false);
    } finally {
      setTesting(false);
    }
  };

  // تحديث إعدادات رفع صور الصيانة
  const handleMaintenanceImageChange = (field: string, value: any) => {
    setMaintenanceImageSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // حفظ إعدادات رفع صور الصيانة في Firebase
  const handleSaveMaintenanceImages = async () => {
    try {
      setSavingMaintenanceImages(true);
      console.log('💾 حفظ إعدادات صور الصيانة في Firebase:', maintenanceImageSettings);

      await saveMaintenanceImageSettings(maintenanceImageSettings);

      setSnackbar({
        open: true,
        message: 'تم حفظ إعدادات رفع صور الصيانة بنجاح في Firebase',
        severity: 'success'
      });

      console.log('✅ تم حفظ إعدادات صور الصيانة بنجاح');
    } catch (error) {
      console.error('❌ خطأ في حفظ إعدادات صور الصيانة:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ إعدادات رفع صور الصيانة',
        severity: 'error'
      });
    } finally {
      setSavingMaintenanceImages(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h5" component="h1">
            الإعدادات المتقدمة
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          هذه الصفحة تحتوي على إعدادات متقدمة للنظام. يرجى توخي الحذر عند تغيير هذه الإعدادات.
        </Typography>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="إعدادات تخزين وحماية الصور" {...a11yProps(0)} />
          <Tab label="إعدادات ImgBB" {...a11yProps(1)} />
          <Tab label="إعدادات ImageKit.io" {...a11yProps(2)} />
          <Tab label="إعدادات رفع صور الصيانة" {...a11yProps(3)} />
          <Tab label="إعدادات بنك الصيانة" {...a11yProps(4)} />
          <Tab label="إعدادات Firebase" {...a11yProps(5)} />
          <Tab label="إعدادات Dropbox" {...a11yProps(6)} />
          <Tab label="إعادة تعيين قاعدة البيانات" {...a11yProps(7)} />
        </Tabs>

        {/* تبويب إعدادات تخزين الصور */}
        <TabPanel value={activeTab} index={0}>
          {loadingImageStorage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                اختر طريقة تخزين الصور المفضلة لديك. يمكنك استخدام خدمات سحابية مثل ImgBB أو ImageKit.io لتخزين الصور.
              </Alert>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  طريقة تخزين الصور وحمايتها
                </Typography>
                <RadioGroup
                  name="storageType"
                  value={imageStorageSettings.storageType}
                  onChange={handleImageStorageChange}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        خيارات التخزين الأساسية
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Radio />}
                        value={ImageStorageType.IMGBB}
                        label="ImgBB فقط"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 4, mb: 2 }}>
                        تخزين الصور على ImgBB فقط. يتطلب إعداد حساب ImgBB. سيفشل تحميل الصور إذا لم يكن هناك اتصال بـ ImgBB.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Radio />}
                        value={ImageStorageType.IMAGEKIT}
                        label="ImageKit.io فقط"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 4, mb: 2 }}>
                        تخزين الصور على ImageKit.io فقط. يتطلب إعداد حساب ImageKit.io. سيفشل تحميل الصور إذا لم يكن هناك اتصال بـ ImageKit.io.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="primary" gutterBottom sx={{ mt: 2 }}>
                        خيارات التخزين المتقدمة
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Radio />}
                        value={ImageStorageType.IMGBB_AND_IMAGEKIT}
                        label="تخزين على ImgBB و ImageKit.io معًا"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 4, mb: 2 }}>
                        تخزين الصور على كل من ImgBB و ImageKit.io في نفس الوقت. يوفر نسخة احتياطية في حالة فشل أحد الخدمات. يتم عرض الصور من ImgBB بشكل افتراضي.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Radio />}
                        value={ImageStorageType.DISPLAY_FROM_IMGBB}
                        label="تخزين على جميع الخدمات مع العرض من ImgBB"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 4, mb: 2 }}>
                        تخزين الصور على جميع الخدمات المتاحة، مع عرض الصور من ImgBB. يتطلب إعداد خدمة ImgBB على الأقل.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Radio />}
                        value={ImageStorageType.DISPLAY_FROM_IMAGEKIT}
                        label="تخزين على جميع الخدمات مع العرض من ImageKit.io"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 4, mb: 2 }}>
                        تخزين الصور على جميع الخدمات المتاحة، مع عرض الصور من ImageKit.io. يتطلب إعداد خدمة ImageKit.io على الأقل.
                      </Typography>
                    </Grid>
                  </Grid>
                </RadioGroup>
              </Paper>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      خدمة تخزين الصور
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <RadioGroup
                      name="storageType"
                      value={imageStorageSettings.storageType}
                      onChange={handleImageStorageChange}
                    >
                      <FormControlLabel
                        value={ImageStorageType.IMGBB}
                        control={<Radio />}
                        label="ImgBB فقط"
                      />
                      <FormControlLabel
                        value={ImageStorageType.IMAGEKIT}
                        control={<Radio />}
                        label="ImageKit.io فقط"
                      />
                      <FormControlLabel
                        value={ImageStorageType.IMGBB_AND_IMAGEKIT}
                        control={<Radio />}
                        label="ImgBB و ImageKit.io معاً (العرض من ImgBB افتراضياً)"
                      />
                      <FormControlLabel
                        value={ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL}
                        control={<Radio />}
                        label="ImgBB و ImageKit.io معاً (اختيار مصدر العرض)"
                      />
                    </RadioGroup>

                    {imageStorageSettings.storageType === ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          خدمة العرض المفضلة
                        </Typography>
                        <RadioGroup
                          name="preferredDisplaySource"
                          value={imageStorageSettings.preferredDisplaySource || 'imgbb'}
                          onChange={handleImageStorageChange}
                        >
                          <FormControlLabel
                            value="imgbb"
                            control={<Radio />}
                            label="ImgBB"
                          />
                          <FormControlLabel
                            value="imagekit"
                            control={<Radio />}
                            label="ImageKit.io"
                          />
                        </RadioGroup>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      حماية الصور
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <RadioGroup
                      name="imageProtection"
                      value={imageStorageSettings.imageProtection || ImageProtectionType.NONE}
                      onChange={handleImageStorageChange}
                    >
                      <FormControlLabel
                        value={ImageProtectionType.NONE}
                        control={<Radio />}
                        label="بدون حماية"
                      />
                      <FormControlLabel
                        value={ImageProtectionType.PREVENT_SAVE}
                        control={<Radio />}
                        label="منع الحفظ فقط"
                      />
                      <FormControlLabel
                        value={ImageProtectionType.PREVENT_OPEN}
                        control={<Radio />}
                        label="منع الفتح في صفحة مستقلة"
                      />
                      <FormControlLabel
                        value={ImageProtectionType.FULL}
                        control={<Radio />}
                        label="حماية كاملة (منع الحفظ والفتح)"
                      />
                    </RadioGroup>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        علامة مائية (اختياري)
                      </Typography>
                      <TextField
                        name="watermarkText"
                        label="نص العلامة المائية"
                        fullWidth
                        value={imageStorageSettings.watermarkText || ''}
                        onChange={handleImageStorageChange}
                        placeholder="مثال: © اسم المتجر - جميع الحقوق محفوظة"
                        helperText="سيظهر هذا النص على الصور عند تفعيل الحماية"
                        margin="normal"
                      />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveImageStorage}
                      disabled={savingImageStorage}
                    >
                      {savingImageStorage ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          جاري الحفظ...
                        </>
                      ) : (
                        'حفظ إعدادات تخزين وحماية الصور'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>

        {/* تبويب إعدادات ImgBB */}
        <TabPanel value={activeTab} index={1}>
          {loadingImgBB ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                قم بإدخال مفتاح API الخاص بـ ImgBB لتمكين رفع الصور إلى السحابة. يمكنك الحصول على مفتاح API مجاني من موقع ImgBB.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="API Key"
                    name="apiKey"
                    value={imgbbSettings.apiKey}
                    onChange={handleImgBBChange}
                    fullWidth
                    margin="normal"
                    helperText="مفتاح API الخاص بـ ImgBB"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={imgbbSettings.enabled}
                        onChange={handleImgBBChange}
                        name="enabled"
                      />
                    }
                    label="تفعيل ImgBB"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveImgBB}
                  disabled={savingImgBB}
                >
                  {savingImgBB ? <CircularProgress size={24} /> : 'حفظ إعدادات ImgBB'}
                </Button>
              </Box>
            </>
          )}
        </TabPanel>

        {/* تبويب إعدادات ImageKit.io */}
        <TabPanel value={activeTab} index={2}>
          {loadingImageKit ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                قم بإدخال بيانات حساب ImageKit.io الخاص بك لتمكين رفع الصور إلى السحابة. يمكنك الحصول على مفاتيح API من لوحة تحكم ImageKit.io.
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Public Key"
                    name="publicKey"
                    value={imagekitSettings.publicKey}
                    onChange={handleImageKitChange}
                    fullWidth
                    margin="normal"
                    helperText="المفتاح العام لـ ImageKit.io"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Private Key"
                    name="privateKey"
                    value={imagekitSettings.privateKey}
                    onChange={handleImageKitChange}
                    fullWidth
                    margin="normal"
                    type="password"
                    helperText="المفتاح الخاص لـ ImageKit.io"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="URL Endpoint"
                    name="urlEndpoint"
                    value={imagekitSettings.urlEndpoint}
                    onChange={handleImageKitChange}
                    fullWidth
                    margin="normal"
                    helperText="نقطة نهاية URL لـ ImageKit.io (مثال: https://ik.imagekit.io/your_imagekit_id)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={imagekitSettings.enabled}
                        onChange={handleImageKitChange}
                        name="enabled"
                      />
                    }
                    label="تفعيل ImageKit.io"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveImageKit}
                  disabled={savingImageKit}
                >
                  {savingImageKit ? <CircularProgress size={24} /> : 'حفظ إعدادات ImageKit.io'}
                </Button>
              </Box>
            </>
          )}
        </TabPanel>

        {/* تبويب إعدادات تحميل وحماية الصور */}
        <TabPanel value={activeTab} index={3}>
          {loadingImageStorage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                هنا يمكنك تكوين إعدادات تحميل الصور وحمايتها من النسخ والحفظ غير المصرح به
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      خدمة تخزين الصور
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <RadioGroup
                      name="storageType"
                      value={imageStorageSettings.storageType}
                      onChange={handleImageStorageChange}
                    >
                      <FormControlLabel
                        value={ImageStorageType.IMGBB}
                        control={<Radio />}
                        label="ImgBB فقط"
                      />
                      <FormControlLabel
                        value={ImageStorageType.IMAGEKIT}
                        control={<Radio />}
                        label="ImageKit.io فقط"
                      />
                      <FormControlLabel
                        value={ImageStorageType.IMGBB_AND_IMAGEKIT}
                        control={<Radio />}
                        label="ImgBB و ImageKit.io معاً (العرض من ImgBB افتراضياً)"
                      />
                      <FormControlLabel
                        value={ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL}
                        control={<Radio />}
                        label="ImgBB و ImageKit.io معاً (اختيار مصدر العرض)"
                      />
                    </RadioGroup>

                    {imageStorageSettings.storageType === ImageStorageType.IMGBB_AND_IMAGEKIT_DUAL && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          خدمة العرض المفضلة
                        </Typography>
                        <RadioGroup
                          name="preferredDisplaySource"
                          value={imageStorageSettings.preferredDisplaySource || 'imgbb'}
                          onChange={handleImageStorageChange}
                        >
                          <FormControlLabel
                            value="imgbb"
                            control={<Radio />}
                            label="ImgBB"
                          />
                          <FormControlLabel
                            value="imagekit"
                            control={<Radio />}
                            label="ImageKit.io"
                          />
                        </RadioGroup>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      حماية الصور
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <RadioGroup
                      name="imageProtection"
                      value={imageStorageSettings.imageProtection || ImageProtectionType.NONE}
                      onChange={handleImageStorageChange}
                    >
                      <FormControlLabel
                        value={ImageProtectionType.NONE}
                        control={<Radio />}
                        label="بدون حماية"
                      />
                      <FormControlLabel
                        value={ImageProtectionType.PREVENT_SAVE}
                        control={<Radio />}
                        label="منع الحفظ فقط"
                      />
                      <FormControlLabel
                        value={ImageProtectionType.PREVENT_OPEN}
                        control={<Radio />}
                        label="منع الفتح في صفحة مستقلة"
                      />
                      <FormControlLabel
                        value={ImageProtectionType.FULL}
                        control={<Radio />}
                        label="حماية كاملة (منع الحفظ والفتح)"
                      />
                    </RadioGroup>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        علامة مائية (اختياري)
                      </Typography>
                      <TextField
                        name="watermarkText"
                        label="نص العلامة المائية"
                        fullWidth
                        value={imageStorageSettings.watermarkText || ''}
                        onChange={handleImageStorageChange}
                        placeholder="مثال: © اسم المتجر - جميع الحقوق محفوظة"
                        helperText="سيظهر هذا النص على الصور عند تفعيل الحماية"
                        margin="normal"
                      />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveImageStorage}
                      disabled={savingImageStorage}
                    >
                      {savingImageStorage ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          جاري الحفظ...
                        </>
                      ) : (
                        'حفظ إعدادات تخزين وحماية الصور'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>

        {/* تبويب إعدادات رفع صور الصيانة */}
        <TabPanel value={activeTab} index={3}>
          {loadingMaintenanceImages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  إعدادات رفع صور الصيانة
                </Typography>
                <Typography variant="body2">
                  هذه الإعدادات تتحكم في كيفية التقاط ورفع صور الأجهزة والعيوب في طلبات الصيانة.
                  يمكنك اختيار مزود الخدمة المفضل وضبط جودة الصور.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                {/* اختيار مزود الخدمة */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      مزود خدمة رفع الصور
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <RadioGroup
                      value={maintenanceImageSettings.provider}
                      onChange={(e) => handleMaintenanceImageChange('provider', e.target.value)}
                    >
                      <FormControlLabel
                        value="imgbb"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2">ImgBB</Typography>
                            <Typography variant="caption" color="text.secondary">
                              خدمة مجانية وسهلة الاستخدام
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="imagekit"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2">ImageKit</Typography>
                            <Typography variant="caption" color="text.secondary">
                              خدمة متقدمة مع تحسين تلقائي
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="cloudinary"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2">Cloudinary</Typography>
                            <Typography variant="caption" color="text.secondary">
                              منصة شاملة للوسائط
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </Paper>
                </Grid>

                {/* إعدادات الضغط */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      إعدادات ضغط الصور
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={maintenanceImageSettings.compressionEnabled}
                          onChange={(e) => handleMaintenanceImageChange('compressionEnabled', e.target.checked)}
                        />
                      }
                      label="تفعيل ضغط الصور"
                      sx={{ mb: 2 }}
                    />

                    {maintenanceImageSettings.compressionEnabled && (
                      <>
                        <TextField
                          label="جودة الضغط (%)"
                          type="number"
                          value={maintenanceImageSettings.compressionQuality}
                          onChange={(e) => handleMaintenanceImageChange('compressionQuality', Number(e.target.value))}
                          inputProps={{ min: 10, max: 100 }}
                          fullWidth
                          margin="normal"
                          helperText="كلما قل الرقم، قل حجم الملف وقلت الجودة"
                        />

                        <TextField
                          label="الحد الأقصى للعرض (بكسل)"
                          type="number"
                          value={maintenanceImageSettings.maxImageSize}
                          onChange={(e) => handleMaintenanceImageChange('maxImageSize', Number(e.target.value))}
                          inputProps={{ min: 480, max: 4096 }}
                          fullWidth
                          margin="normal"
                          helperText="سيتم تصغير الصور الأكبر من هذا الحد"
                        />
                      </>
                    )}
                  </Paper>
                </Grid>

                {/* إعدادات إضافية */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      إعدادات إضافية
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={maintenanceImageSettings.autoUpload}
                          onChange={(e) => handleMaintenanceImageChange('autoUpload', e.target.checked)}
                        />
                      }
                      label="رفع تلقائي للصور بعد التقاطها"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                      سيتم رفع الصور تلقائياً بدلاً من انتظار الضغط على زر الحفظ
                    </Typography>

                    {/* مزود الخدمة البديل */}
                    <Typography variant="subtitle1" gutterBottom>
                      مزود الخدمة البديل
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      سيتم استخدامه في حالة فشل المزود الأساسي
                    </Typography>
                    <RadioGroup
                      value={maintenanceImageSettings.fallbackProvider}
                      onChange={(e) => handleMaintenanceImageChange('fallbackProvider', e.target.value)}
                      row
                    >
                      <FormControlLabel
                        value="imgbb"
                        control={<Radio />}
                        label="ImgBB"
                      />
                      <FormControlLabel
                        value="imagekit"
                        control={<Radio />}
                        label="ImageKit"
                      />
                      <FormControlLabel
                        value="cloudinary"
                        control={<Radio />}
                        label="Cloudinary"
                      />
                    </RadioGroup>

                    {/* عدد محاولات الإعادة */}
                    <TextField
                      label="عدد محاولات الإعادة"
                      type="number"
                      value={maintenanceImageSettings.retryAttempts}
                      onChange={(e) => handleMaintenanceImageChange('retryAttempts', Number(e.target.value))}
                      inputProps={{ min: 1, max: 5 }}
                      fullWidth
                      margin="normal"
                      helperText="عدد المحاولات عند فشل رفع الصورة"
                      sx={{ mt: 2 }}
                    />
                  </Paper>
                </Grid>

                {/* زر الحفظ */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveMaintenanceImages}
                      disabled={savingMaintenanceImages}
                    >
                      {savingMaintenanceImages ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          جاري الحفظ...
                        </>
                      ) : (
                        'حفظ إعدادات رفع صور الصيانة'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>

        {/* تبويب إعدادات بنك الصيانة */}
        <TabPanel value={activeTab} index={4}>
          {loadingMaintenanceBank ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  إعدادات بنك الصيانة
                </Typography>
                <Typography variant="body2">
                  هذه الإعدادات تتحكم في كيفية رفع وإدارة صور الأعطال وبدائل الكوفات في بنك الصيانة.
                  يمكنك اختيار مزود الخدمة المفضل وضبط إعدادات الرفع والحماية.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  <strong>ملاحظة:</strong> يمكنك الآن استخدام نفس مفاتيح API المُعرَّفة في إعدادات تخزين الصور بدلاً من إدخال مفاتيح منفصلة، مما يوفر الوقت والجهد.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                {/* إعدادات مزود الخدمة */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          مزود خدمة رفع الصور
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<TestIcon />}
                          onClick={handleTestImageSettings}
                          disabled={testing}
                        >
                          {testing ? 'جاري الاختبار...' : 'اختبار الإعدادات'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          startIcon={<CloudDoneIcon />}
                          onClick={handleCopyProductSettings}
                          disabled={testing}
                        >
                          نسخ إعدادات المنتجات
                        </Button>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <RadioGroup
                      value={maintenanceBankSettings.imageProvider}
                      onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, imageProvider: e.target.value as 'imgbb' | 'imagekit' }))}
                    >
                      <FormControlLabel
                        value="imgbb"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2">ImgBB</Typography>
                            <Typography variant="caption" color="text.secondary">
                              خدمة مجانية وسهلة الاستخدام
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="imagekit"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2">ImageKit</Typography>
                            <Typography variant="caption" color="text.secondary">
                              خدمة متقدمة مع تحسين تلقائي وإمكانيات التعديل
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>

                    {/* خيار استخدام إعدادات تخزين الصور */}
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={maintenanceBankSettings.useImageStorageSettings}
                            onChange={(e) => setMaintenanceBankSettings(prev => ({
                              ...prev,
                              useImageStorageSettings: e.target.checked,
                              // مسح مفاتيح API المنفصلة عند التبديل لاستخدام إعدادات تخزين الصور
                              imgbbApiKey: e.target.checked ? '' : prev.imgbbApiKey,
                              imagekitPublicKey: e.target.checked ? '' : prev.imagekitPublicKey,
                              imagekitPrivateKey: e.target.checked ? '' : prev.imagekitPrivateKey,
                              imagekitUrlEndpoint: e.target.checked ? '' : prev.imagekitUrlEndpoint
                            }))}
                          />
                        }
                        label="استخدام إعدادات تخزين الصور"
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        استخدم نفس مفاتيح API المُعرَّفة في إعدادات تخزين الصور بدلاً من إدخال مفاتيح منفصلة
                      </Typography>
                    </Box>

                    {/* إعدادات ImgBB */}
                    {maintenanceBankSettings.imageProvider === 'imgbb' && !maintenanceBankSettings.useImageStorageSettings && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          label="ImgBB API Key"
                          value={maintenanceBankSettings.imgbbApiKey}
                          onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, imgbbApiKey: e.target.value }))}
                          type="password"
                          helperText="احصل على مفتاح API من imgbb.com"
                          margin="normal"
                        />
                      </Box>
                    )}

                    {/* إعدادات ImageKit */}
                    {maintenanceBankSettings.imageProvider === 'imagekit' && !maintenanceBankSettings.useImageStorageSettings && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          label="ImageKit Public Key"
                          value={maintenanceBankSettings.imagekitPublicKey}
                          onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, imagekitPublicKey: e.target.value }))}
                          helperText="المفتاح العام من ImageKit"
                          margin="normal"
                        />
                        <TextField
                          fullWidth
                          label="ImageKit Private Key"
                          value={maintenanceBankSettings.imagekitPrivateKey}
                          onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, imagekitPrivateKey: e.target.value }))}
                          type="password"
                          helperText="المفتاح الخاص من ImageKit"
                          margin="normal"
                        />
                        <TextField
                          fullWidth
                          label="ImageKit URL Endpoint"
                          value={maintenanceBankSettings.imagekitUrlEndpoint}
                          onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, imagekitUrlEndpoint: e.target.value }))}
                          helperText="مثال: https://ik.imagekit.io/your_id"
                          margin="normal"
                        />
                      </Box>
                    )}

                    {/* عرض مفاتيح API من إعدادات تخزين الصور */}
                    {maintenanceBankSettings.useImageStorageSettings && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          مفاتيح API من إعدادات تخزين الصور:
                        </Typography>
                        {maintenanceBankSettings.imageProvider === 'imgbb' && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>ImgBB API Key:</strong> {imgbbSettings.apiKey ? 'تم التكوين' : 'غير مُكوَّن'}
                            </Typography>
                          </Box>
                        )}
                        {maintenanceBankSettings.imageProvider === 'imagekit' && (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>ImageKit Public Key:</strong> {imagekitSettings.publicKey ? 'تم التكوين' : 'غير مُكوَّن'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>ImageKit Private Key:</strong> {imagekitSettings.privateKey ? 'تم التكوين' : 'غير مُكوَّن'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>ImageKit URL Endpoint:</strong> {imagekitSettings.urlEndpoint ? 'تم التكوين' : 'غير مُكوَّن'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* إعدادات الرفع */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      إعدادات الرفع والضغط
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <TextField
                      fullWidth
                      label="الحد الأقصى لحجم الصورة (ميجابايت)"
                      type="number"
                      value={maintenanceBankSettings.maxImageSize}
                      onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, maxImageSize: Number(e.target.value) }))}
                      inputProps={{ min: 1, max: 10 }}
                      margin="normal"
                      helperText="الحد الأقصى المسموح لحجم الصورة"
                    />

                    <TextField
                      fullWidth
                      label="الحد الأقصى للصور لكل موضوع"
                      type="number"
                      value={maintenanceBankSettings.maxImagesPerTopic}
                      onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, maxImagesPerTopic: Number(e.target.value) }))}
                      inputProps={{ min: 1, max: 20 }}
                      margin="normal"
                      helperText="عدد الصور المسموح لكل موضوع في بنك الصيانة"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={maintenanceBankSettings.compressionEnabled}
                          onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, compressionEnabled: e.target.checked }))}
                        />
                      }
                      label="تفعيل ضغط الصور"
                      sx={{ mt: 2, mb: 1 }}
                    />

                    {maintenanceBankSettings.compressionEnabled && (
                      <TextField
                        fullWidth
                        label="جودة الضغط (%)"
                        type="number"
                        value={maintenanceBankSettings.compressionQuality}
                        onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, compressionQuality: Number(e.target.value) }))}
                        inputProps={{ min: 10, max: 100 }}
                        margin="normal"
                        helperText="كلما قل الرقم، قل حجم الملف وقلت الجودة"
                      />
                    )}
                  </Paper>
                </Grid>

                {/* إعدادات النسخ الاحتياطي والأمان */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      إعدادات النسخ الاحتياطي والأمان
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={maintenanceBankSettings.autoBackup}
                              onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, autoBackup: e.target.checked }))}
                            />
                          }
                          label="النسخ الاحتياطي التلقائي"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          نسخ احتياطي تلقائي لبيانات بنك الصيانة
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={maintenanceBankSettings.requireApproval}
                              onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, requireApproval: e.target.checked }))}
                            />
                          }
                          label="تتطلب موافقة قبل النشر"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          المواضيع الجديدة تحتاج موافقة المشرف
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={maintenanceBankSettings.allowPublicAccess}
                              onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, allowPublicAccess: e.target.checked }))}
                            />
                          }
                          label="السماح بالوصول العام"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          إمكانية الوصول لبنك الصيانة بدون تسجيل دخول
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          تكرار النسخ الاحتياطي
                        </Typography>
                        <RadioGroup
                          value={maintenanceBankSettings.backupFrequency}
                          onChange={(e) => setMaintenanceBankSettings(prev => ({ ...prev, backupFrequency: e.target.value as 'daily' | 'weekly' | 'monthly' }))}
                          row
                        >
                          <FormControlLabel value="daily" control={<Radio />} label="يومي" />
                          <FormControlLabel value="weekly" control={<Radio />} label="أسبوعي" />
                          <FormControlLabel value="monthly" control={<Radio />} label="شهري" />
                        </RadioGroup>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          أنواع الملفات المسموحة
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {['JPEG', 'PNG', 'GIF', 'WebP'].map((type) => (
                            <Chip
                              key={type}
                              label={type}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          أنواع الصور المدعومة في بنك الصيانة
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* أزرار الحفظ والاختبار */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={testMaintenanceBankConnection}
                      disabled={savingMaintenanceBank}
                    >
                      اختبار الاتصال
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={saveMaintenanceBankSettingsHandler}
                      disabled={savingMaintenanceBank}
                    >
                      {savingMaintenanceBank ? (
                        <>
                          <CircularProgress size={24} sx={{ mr: 1 }} />
                          جاري الحفظ...
                        </>
                      ) : (
                        'حفظ إعدادات بنك الصيانة'
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>

        {/* تبويب إعدادات Firebase */}
        <TabPanel value={activeTab} index={5}>
          {loadingFirebase ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  تحذير: تغيير هذه الإعدادات قد يؤدي إلى فقدان الاتصال بقاعدة البيانات!
                </Typography>
                <Typography variant="body2">
                  يجب أن تكون متأكداً من صحة بيانات Firebase الجديدة قبل حفظها.
                  سيتطلب الأمر إعادة تشغيل التطبيق لتطبيق التغييرات.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="API Key"
                    name="apiKey"
                    value={firebaseSettings.apiKey}
                    onChange={handleFirebaseChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Auth Domain"
                    name="authDomain"
                    value={firebaseSettings.authDomain}
                    onChange={handleFirebaseChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Project ID"
                    name="projectId"
                    value={firebaseSettings.projectId}
                    onChange={handleFirebaseChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Storage Bucket"
                    name="storageBucket"
                    value={firebaseSettings.storageBucket}
                    onChange={handleFirebaseChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Messaging Sender ID"
                    name="messagingSenderId"
                    value={firebaseSettings.messagingSenderId}
                    onChange={handleFirebaseChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="App ID"
                    name="appId"
                    value={firebaseSettings.appId}
                    onChange={handleFirebaseChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveFirebase}
                  disabled={savingFirebase}
                >
                  {savingFirebase ? <CircularProgress size={24} /> : 'حفظ إعدادات Firebase'}
                </Button>
              </Box>
            </>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  للحصول على رمز الوصول، قم بزيارة{' '}
                  <a
                    href="https://www.dropbox.com/developers/apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    Dropbox Developers
                  </a>
                  {' '}وقم بإنشاء تطبيق جديد. ثم انسخ رمز الوصول من إعدادات التطبيق.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="رمز الوصول"
                name="accessToken"
                value={settings.accessToken}
                onChange={handleChange}
                type="password"
                helperText="رمز الوصول الخاص بتطبيق Dropbox"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="اسم المجلد"
                name="folderName"
                value={settings.folderName}
                onChange={handleChange}
                helperText="اسم المجلد الذي سيتم حفظ النسخ الاحتياطية فيه"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="الحد الأقصى للنسخ الاحتياطية"
                name="maxBackups"
                type="number"
                value={settings.maxBackups}
                onChange={handleChange}
                helperText="الحد الأقصى لعدد النسخ الاحتياطية المحفوظة"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enabled}
                    onChange={handleChange}
                    name="enabled"
                  />
                }
                label="تفعيل النسخ الاحتياطي في Dropbox"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoUpload}
                    onChange={handleChange}
                    name="autoUpload"
                  />
                }
                label="التحميل التلقائي للنسخ الاحتياطية"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoDelete}
                    onChange={handleChange}
                    name="autoDelete"
                  />
                }
                label="حذف النسخ الاحتياطية القديمة تلقائياً"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={isDropboxConnected ? <CloudDoneIcon /> : <CloudOffIcon />}
                  onClick={handleTestConnection}
                  disabled={testing || !settings.accessToken}
                >
                  {testing ? 'جاري الاختبار...' : (isDropboxConnected ? 'متصل' : 'اختبار الاتصال')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        {/* تبويب إعادة تعيين قاعدة البيانات */}
        <TabPanel value={activeTab} index={7}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              تحذير: هذه العملية لا يمكن التراجع عنها!
            </Typography>
            <Typography variant="body2">
              سيؤدي إعادة تعيين قاعدة البيانات إلى حذف جميع البيانات الموجودة في النظام بشكل نهائي.
              يرجى التأكد من عمل نسخة احتياطية قبل المتابعة.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleOpenResetDialog}
              size="large"
            >
              إعادة تعيين قاعدة البيانات
            </Button>
          </Box>
        </TabPanel>
      </Paper>

      {/* نافذة حوار تأكيد إعادة تعيين قاعدة البيانات */}
      <Dialog
        open={openResetDialog}
        onClose={handleCloseResetDialog}
        aria-labelledby="reset-dialog-title"
      >
        <DialogTitle id="reset-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="error" sx={{ mr: 1 }} />
            تأكيد إعادة تعيين قاعدة البيانات
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            هذه العملية ستؤدي إلى حذف جميع البيانات في النظام بشكل نهائي ولا يمكن التراجع عنها.
            للتأكيد، يرجى الإجابة على السؤال التالي:
          </DialogContentText>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" align="center">
              {question}
            </Typography>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label="الإجابة"
            type="text"
            fullWidth
            value={resetConfirmation}
            onChange={(e) => setResetConfirmation(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} color="inherit">
            إلغاء
          </Button>
          <Button
            onClick={handleResetDatabase}
            color="error"
            variant="contained"
            disabled={resetting || !resetConfirmation}
          >
            {resetting ? <CircularProgress size={24} /> : 'تأكيد إعادة التعيين'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* الإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdvancedSettingsPage;
